// ─────────────────────────────────────────────────────────────────────────────
// surface.ts — THE DECLARED SURFACE. Nine roots, twenty-eight sub-tabs.
//
// OWNER RULING (in-terminal, 2026-08-19): "STOP WAITING ON ROOTS. BUILD THE
// PAGES. A backend that has not restarted must not gate a single page. If the
// backend returns nothing, the pages still render — with honest empty values.
// THE STRUCTURE IS NOT DATA. It is the app."
//
// THAT RULING SPLITS TWO THINGS THAT WERE PREVIOUSLY ONE:
//
//   STRUCTURE — which roots exist, which sub-tabs each carries, which panels sit
//               in each. This is the APP. It is declared HERE, sourced from the
//               design, and it renders whether or not any backend answers.
//   DATA      — what goes IN a panel. That still comes from the API and is still
//               honestly empty until a write lands. Nothing below is data.
//
// It does NOT license naming an agent or a domain. Nothing in this file does
// either: an agent's identity still comes from the registry projection, and a
// controller reaches its root through its DECLARED ROLE (see ROLE_ROOT), never
// through a name written here.
//
// PARITY, NOT DUPLICATION: core/harness_topology.py declares the same nine root
// LABELS for the GROWTH board, because the board store is keyed by their slugs.
// Those two declarations must agree — the slug is what a panel's data is read
// under. `assertParity()` below is the check; it is exported so a test or a
// review can run it rather than trust that they match.
//
// THE HTML'S PAGE-HARNESS ROOT IS NOT HERE. Owner ruling: nine roots, not ten —
// the harness stays internal and is not a reopenable location.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// PANEL BODIES — the SHAPE of a panel, transcribed from the design HTML.
//
// A table's COLUMNS are structure; its ROWS are data. A KPI tile's LABEL is
// structure; its VALUE is data. A metric row's NAME is structure; its FIGURE is
// data. That line is drawn once, here, and it is why nothing below carries a
// number: every figure in the HTML is sample data (owner ruling), so the columns,
// labels and names are taken and the values are left to the board.
// ─────────────────────────────────────────────────────────────────────────────

export type PanelBody =
  /** A table: header row from the design, body from the board.
   *
   *  `cells` names the columns the design renders as something other than plain
   *  text, keyed by the column label the same table declares — so a renamed
   *  column stops being styled rather than styling the wrong one silently.
   *  HOW A CELL IS TONED IS NOT DECLARED HERE. Both mechanisms — the sampled
   *  word vocabulary (STATE_PILLS) and the measurement bands (MEASURE_SLOTS) —
   *  are keyed panel+slot in presentation.ts. Declaring an opt-in beside the
   *  columns as well would mean a rule could be added and silently not render
   *  because the flag next to it was forgotten. One source, nothing to sync. */
  | {
      kind: 'table';
      columns: string[];
      /** THE DESIGN DRAWS TABLES TWO WAYS and this says which. `.backend-table`
       *  is the operational one: its FIRST COLUMN IS BOLD and its cells may carry
       *  a second line. Eight panels use it. That is the entire difference — the
       *  CSS is two rules and they are the whole specification.
       *
       *  A CELL'S SUB-LINE IS BOARD DATA, not declared here: a cell arrives as a
       *  bare value (every cell today) or as { value, sub }, exactly as a KPI
       *  cell arrives bare or as { value, direction }. So this flag is presentation
       *  of a shape the design draws, never a claim about what a cell holds. */
      backend?: true;
      /** TWO-STAGE LANE FILTER — REGION, THEN SECTOR. Opt-in per table, because
       *  it is A CLAIM ABOUT THE ROWS: only a table whose rows are companies
       *  carrying a lane can be narrowed by one. `qualified_lists` is keyed on
       *  campaign_id, holds two rows and carries no company lane, so it does NOT
       *  declare this — a dropdown there would filter nothing, which is a control
       *  that lies.
       *
       *  WHAT IT FILTERS BY IS NOT DECLARED HERE, and that is the point. The
       *  options are derived from the rows themselves (`laneFilter.ts`), so there
       *  is no sector list in this file and no state list — nothing to keep in
       *  step with `server/growth_lane.SECTORS`, which no route publishes. */
      lanes?: true;
      /** THE ONLY INTERACTIVE ELEMENT A TABLE MAY CARRY, and only on the rows
       *  that earn it. `column` names the cell that renders as a button; `when`
       *  names the row key that must be boolean true for it to.
       *
       *  BOTH ARE THE PRODUCER'S OWN NAMES. The renderer never decides which row
       *  is ready — it reads the field the producer computes and asks nothing
       *  else, so readiness has one author. Absent field ⇒ no button, which is
       *  the honest state while the producer still drops it. */
      action?: { column: string; when: string };
    }
  /** THE LIVE CALL TILES — the design's own four, in a 1fr 1fr grid that
   *  collapses to one column. Label and sub are the design's words; the figure
   *  is the board's and reads '—' absent. A DIFFERENT ELEMENT FROM `kpis`: the
   *  call tile is blue-washed and sits inside a panel, where a KPI strip is the
   *  page's own header row. Two shapes, two kinds. */
  | { kind: 'callTiles'; tiles: { label: string; sub: string }[] }
  /** THE MESSAGE CONSOLE — a live call/message feed, entirely board-fed:
   *  time, title, detail and a state. The state's vocabulary is sampled per
   *  panel like every other. */
  | { kind: 'messages' }
  /** A KPI strip. `sub` is the design's own descriptor beneath the label. */
  | { kind: 'kpis'; tiles: { label: string; sub?: string }[] }
  /** Label/figure rows. The labels are the design's; the figures are the board's. */
  | { kind: 'metrics'; rows: string[] }
  /** A fixed checklist — doctrine text, not a measurement, so it renders in full. */
  | { kind: 'checklist'; items: string[] }
  /** Named workers/lanes as pills. Labels, not figures. */
  | { kind: 'pills'; items: string[] }
  /** A geographic board. Pins are data; the board is structure. */
  | { kind: 'map' }
  /** A RECORD-CARD LIST. Name, detail and an optional tag per card, all board-fed.
   *
   *  The design's only INSTANCES are built at runtime — a template literal
   *  filtered to the P2 connectors — but the SHAPE is fully written down: the
   *  markup, the `.record-card` styling and its own empty state. Owner ruling
   *  2026-08-25: what is runtime-generated is the instances, not the shape.
   *
   *  `empty` IS THE DESIGN'S OWN EMPTY STATE, transcribed rather than written —
   *  and it renders AS A CARD, because that is what the design does with it. */
  | { kind: 'cards'; empty: { name: string; detail: string } }
  /** Standing doctrine text from the design. Structure, never a data claim. */
  | { kind: 'note'; text: string }
  /** The 24-node hourly reporting board. THE HOURS ARE STRUCTURE — all 24 exist
   *  before any pulse fires; which have fired, and what each says, is data. */
  | { kind: 'hours' }
  /** A time-stamped note timeline. Carries an OPTIONAL source line —
   *  who / where / source_kind — when its producer publishes them. */
  | { kind: 'timeline' }
  /**
   * THE ACTIVITY FEED. The one panel on this surface that is NOT board-fed
   * (owner ruling 2026-09-01): it holds its own route, exactly as Rexy's does.
   *
   * That is the requirement, not an exception. This is the surface the owner
   * checks the harness ON — so a panel rendering from a board written by the
   * thing being verified would verify nothing. Activity, pulse, notes,
   * reporting and dreamstate are five separate things in a chain, each
   * standing on its own.
   */
  | { kind: 'activity' }
  /** The daily-report card archive: today's draft plus prior finals. */
  | { kind: 'reports' }
  /** An immutable event ledger: time · event · detail · state. */
  | { kind: 'ledger' }
  /** A compact stat block. Labels are structure; figures are data. */
  | { kind: 'stats'; items: string[] }
  /** A service-health chip ribbon. Names are structure; health is data. */
  | { kind: 'chips'; items: string[] }
  /** A connector/API registry grid, fed entirely by the board. */
  | { kind: 'registry' }
  /** A left-to-right step flow. The STEPS are the process and are structure;
   *  where the work has reached is data. Used for the operating path and for the
   *  five-node pain chain, which is the same shape carrying a different process. */
  | { kind: 'flow'; steps: { label: string; sub?: string }[] }
  /** A funnel: named stages with a proportional bar. Stages are structure. */
  | { kind: 'funnel'; stages: string[] }
  /** A checkable call-task list. Entirely board-fed. */
  | { kind: 'tasks' }
  /** THE SCRIPT SHEET — a LIGHT page inside the dark surface, as the design has
   *  it (#f7f9fb on #13293f). It is a document, not a panel, and reads like one. */
  | { kind: 'script' }
  /** Prioritised questions, each with its own "what it tests" line and its
   *  CATEGORY TAG. All three are TRANSCRIPTION — the design's own words — which
   *  is why `tag` sits here beside them and not in presentation.ts. Owner ruling
   *  2026-08-25: the rule that removed the 22 `cells` declarations governs HOW A
   *  VALUE IS TONED, never what the text says. The tag's TONE is looked up
   *  through the single entry point like every other value; only its text is
   *  declared. REQUIRED, not optional: the design gives every question a tag, so
   *  a missing one should stop the compiler rather than silently drop a pill. */
  | { kind: 'questions'; items: { q: string; tests: string; tag: string }[] }
  /** The six-sheet workbook with its own sheet tabs and per-sheet columns. */
  | { kind: 'workbook'; sheets: { key: string; label: string; columns: string[] }[] }
  /** The design's role banner: a title, its standing description, and pills on
   *  the right. STRUCTURE — the text is the design's own and states what the
   *  surface IS, never what it currently reads. A pill whose `data` key is set
   *  takes its text from the board and shows '—' absent.
   *
   *  NO `tone` HERE. It used to sit inline on each pill, which is a PANEL
   *  DECLARING HOW A VALUE IS TONED — the shape the 22 redundant `cells`
   *  declarations were deleted for, and the one kinds 1-3 all avoided. Owner
   *  ruling 2026-08-25: fix the shape BEFORE multiplying it, because it is a
   *  two-pill change today and a thirty-four-pill change once the 13 remaining
   *  banners are transcribed. The tone now comes from the `banner` slot in
   *  STATE_PILLS, keyed by the pill's label, through the same single entry
   *  point every other value uses. */
  | { kind: 'roleBanner'; title: string; text: string;
      pills: {
        label?: string;
        data?: string;
        /** WHERE THE READING SITS RELATIVE TO ITS WORDS, because the design shows
         *  BOTH and the sample governs: "last sync 10:18" trails, "8 services
         *  healthy" leads. Without this the surface would render the design's own
         *  words in the wrong order for eleven of its pills. Ignored on a pill
         *  with no `data` — standing text has no reading to place. */
        valueFirst?: boolean;
      }[] }
  /** A 16:9 slide grid. Slide COUNT and order are structure; content is data. */
  | { kind: 'slides'; count: number }
  /** Co-pilot suggestion blocks — board-fed, never generated in the surface. */
  | { kind: 'suggestions' }
  /** A mail shell: folder rail plus message list. */
  | { kind: 'inbox' }
  /** A calendar shell: day columns plus entries. */
  | { kind: 'calendar' }
  /** An hourly bar chart. The HOURS are structure; the volume is data. */
  | { kind: 'volume'; hours: string[] }
  /** Expandable call transcripts. Entirely board-fed. */
  | { kind: 'transcripts' }
  /** A connector/plugin grid with health, kept BELOW the operational panels. */
  | { kind: 'plugins' }
  /** An ownership matrix: named groups down the side, agent columns across.
   *  The COLUMNS are the four agent roles; the cells are data. */
  | { kind: 'matrix'; columns: string[] };

/** One panel in a sub-tab (or directly in a root that has no sub-tabs). */
export interface PanelSpec {
  /** Stable identity. Keys the panel's slot in the board root's data. */
  key: string;
  /** What a human reads. Presentation — free to be rewritten. */
  title: string;
  /** The design's own sub-line beneath the title. */
  note?: string;
  /** The design's corner badge on this panel. */
  badge?: string;
  /** The panel's shape. Empty = a plain panel with an honest empty state. */
  body?: PanelBody[];
  /** How wide the panel sits in its grid. Layout only. */
  span?: 'half' | 'full';
  /**
   * CHROME, NOT ONE OF THE ROOT'S PANELS. The design puts a KPI strip above some
   * sub-tabs and a banner above Overview; they render, but the spec's per-root
   * panel count does not include them. Flagged so the build ledger counts what
   * the spec counts — an off-by-one in a ledger is how a root comes to read as
   * finished before it is.
   */
  chrome?: boolean;
  /**
   * RENDERED BY ITS OWN COMPONENT, not by the generic DeclaredPanel.
   *
   * Overview's seven were built first, as bespoke components with live sources
   * (the activity pulse reads /api/rexy/activity; the roster reads the registry).
   * They have no declared `body` because their body is code. Marked so the
   * acceptance check can tell "renders through a dedicated component" apart from
   * "is a heading with nothing under it" — which is the defect it exists to catch.
   */
  custom?: boolean;
}

/**
 * A VIEW inside a sub-tab — the third navigation level.
 *
 * The attached Admin Agent is an AGENT, not two panels hanging off another root:
 * the HTML gives it its own switcher (switchAdminView) with three views, nested
 * inside the receptionist's sixth sub-tab. That nesting is reproduced here rather
 * than flattened, because flattening it is what made the Admin Agent look like a
 * pair of panels the first time.
 */
export interface ViewSpec {
  key: string;
  label: string;
  panels: PanelSpec[];
}

export interface SubTabSpec {
  key: string;
  label: string;
  panels: PanelSpec[];
  /** Nested views. When present, `panels` is ignored and the views render. */
  views?: ViewSpec[];
  /**
   * How many panels the design carries for this sub-tab when the panel list is
   * not yet transcribed from it. NOT a placeholder for content — a COUNT that
   * says exactly how much is outstanding, so a half-built sub-tab can never read
   * as a finished one. Zero (or absent) means the list below is complete.
   */
  awaiting?: number;
}

export interface RootSpec {
  /** Slug — byte-identical to core/harness_topology.slug(label). Keys the board. */
  key: string;
  label: string;
  /** The design's standing note beneath the root's heading. */
  note?: string;
  /** Sub-tabs, in design order. Empty = this root's panels render directly. */
  subTabs: SubTabSpec[];
  /** Panels for a root with no sub-tabs. */
  panels: PanelSpec[];
  awaiting?: number;
}

const P = (key: string, title: string, span?: 'half' | 'full'): PanelSpec =>
  ({ key, title, span, custom: true });
/** A fully-specified panel, transcribed from the design. */
const D = (key: string, title: string, note: string | undefined,
           body: PanelBody[], span?: 'half' | 'full', badge?: string): PanelSpec =>
  ({ key, title, note, body, span, badge });

// ─────────────────────────────────────────────────────────────────────────────
// THE SALES INTELLIGENCE WORKBOOK — DECLARED ONCE, RENDERED ON TWO TABS.
//
// sales_intelligence_master.xlsx is ONE FILE. Marketing writes into it at the
// handoff boundary; Sales reads and works the same file. So this is not the
// workbook MOVED from Sales to Marketing, and it is not a copy: it is one
// declaration referenced from Marketing's tab and from Sales' Companies + Sector
// Workbook tab. When Marketing adds a row it appears in Sales' workbook because
// IT IS THE SAME FILE — there is nothing to sync, and no sync is built.
//
// Declared once ON PURPOSE. Two copies of this array is how the two tabs come to
// disagree about which columns the file has — the same fault as the rail's two
// agent renderers, one of which showed the job title where the name belongs.
//
// FOR THE BACKEND WHEN IT LANDS: the board is addressed per root, so these panels
// read board.marketing.* on one tab and board.sales.* on the other. Both must be
// served from the ONE workbook source. The surface is declared identically and is
// not what could make them differ; the writer must not become what does.
// ─────────────────────────────────────────────────────────────────────────────
export const SALES_WORKBOOK_PANELS: PanelSpec[] = [
  { ...D('workbook_banner', 'Sales Intelligence Workbook', undefined, [{
      kind: 'roleBanner',
      title: 'Sales Intelligence Workbook',
      text: 'Incoming companies and sector intelligence share one terminal-backed '
          + 'Excel file. The interface below mirrors the workbook while Sales '
          + 'Overview remains a read-only rollup.',
      pills: [
        { label: 'sales_intelligence_master.xlsx' },
        { label: 'last sync', data: 'last_sync' },
      ],
    }], 'full'), chrome: true },
  { ...D('workbook_shell', 'Sales workbook', undefined, [{ kind: 'workbook', sheets: [
      { key: 'companies', label: 'Companies', columns: ['A · Company', 'B · Sector',
        'C · Region', 'D · Scale', 'E · Fit', 'F · Crowding', 'G · Stage', 'H · Owner',
        'I · Freshness', 'J · Next action'] },
      { key: 'sectors', label: 'Sector Intelligence', columns: ['A · Sector',
        'B · Economic score', 'C · Current crowding', 'D · Primary pain',
        'E · Qualified company shape', 'F · Active companies', 'G · Consult rate',
        'H · Sales emphasis'] },
      { key: 'contacts', label: 'Contacts', columns: ['Company', 'Buyer role',
        'Public route', 'Email', 'Last known email', 'Phone', 'Route confidence',
        'Owner'] },
      { key: 'scores', label: 'Scores', columns: ['Company', 'Pain 30%',
        'Reception gap 25%', 'Affordability 20%', 'Phone dependence 15%',
        'Evidence 10%', 'Total', 'Downgrade'] },
      { key: 'research', label: 'Research State', columns: ['Company', 'Packet',
        'Web research', 'Sources', 'Pain overlay', 'Buyer map', 'Script', 'Deck'] },
      { key: 'outcomes', label: 'Outcomes', columns: ['Company', 'Last event',
        'Outcome', 'Objection', 'Next touch', 'Decay day', 'CEO state'] },
    ] }], 'full'), chrome: true },
  D('acceptance_gate', 'Selected company acceptance gate',
    'The selected company remains selected across the downstream tabs.',
    [{ kind: 'table', columns: ['Check', 'State', 'Sales action'] }], 'half', 'packet accepted'),
  D('workbook_ownership', 'Workbook ownership',
    'The spreadsheet is an operational system, not permanent sales doctrine.',
    [{ kind: 'metrics', rows: ['Backend writer', 'Owner edits', 'Sales Overview',
      'CRM sync', 'Audit'] }], 'half'),
];

export const SURFACE: RootSpec[] = [
  // ── 1 · OVERVIEW — no sub-tabs, 7 panels ──────────────────────────────────
  // The only root whose panels are BUILT rather than declared-and-awaiting: it
  // shipped first and its seven are real components, wired in MissionControl.
  // The banner is chrome above the panels, not one of the seven.
  {
    key: 'overview', label: 'Overview', subTabs: [],
    panels: [
      P('kpis', 'KPIs', 'full'),
      P('activity_pulse', 'Activity pulse'),
      P('budget_rollup', 'Budget rollup'),
      P('agent_roster', 'Agent roster'),
      P('ceo_report', 'Daily CEO report'),
      P('telco_rollup', 'Telco rollup'),
      P('api_change_watch', 'API change watch'),
    ],
  },

  // ── 2 · CEO — 2 sub-tabs, 13 panels · ALPHA · READ-ONLY ───────────────────
  // Transcribed from page-ceo. 24 hourly pulse nodes; TWO compiled reports a day,
  // at the 07:00 and 19:00 gates (`core/alpha_report.BOUNDARY_HOURS = (7, 19)` —
  // the copy said one, at 18:00, and the harness was never built that way);
  // operational placeholders FIRST and connector metadata BELOW them — the CEO
  // is not a connector configuration page.
  //
  // ⚠ THE KPI TILE LABELS ARE PRODUCER KEYS. `core/alpha_board` publishes
  // `ceo_kpis` keyed BY THE LABEL ("Daily report" is `KPI_DAILY_REPORT`) and
  // `readKpi(data[t.label])` looks it up. Renaming a label SILENTLY EMPTIES its
  // own tile — so corrections go in `sub`, never in `label`.
  {
    key: 'ceo', label: 'CEO', panels: [],
    note: 'Read-only reporting and oversight. The CEO reads calls, transcripts, admin '
        + 'actions, sales and marketing events and system telemetry, then writes its own '
        + 'note — it never becomes the system of record.',
    subTabs: [
      { key: 'ceo_reporting', label: 'CEO Reporting', panels: [
        { ...D('ceo_kpis', 'Reporting totals', undefined, [{ kind: 'kpis', tiles: [
            { label: 'Hourly pulse nodes', sub: 'one node every hour' },
            { label: 'Completed today', sub: 'source-linked notes' },
            { label: 'CEO notes today', sub: 'calls + admin + handoffs' },
            // `label` IS THE PRODUCER KEY (alpha_board.KPI_DAILY_REPORT) — only
            // `sub` may be corrected here. See the warning above.
            { label: 'Daily report', sub: 'two compiled reports a day' },
          ] }], 'full'), chrome: true },
        // ── THE FLOOR THE NODES STAND ON ──────────────────────────────────
        // Directly under the KPI strip and ABOVE the nodes, because it is what
        // the harness read BEFORE it wrote any of them. Its own route, its own
        // data path — never the `ceo` root this page's other panels read.
        D('ceo_activity', 'Activity — what the CEO read from',
          'Every GROWTH change, hour by hour, with the chip that says whether the pulse '
          + 'has actually consumed it. Read from the mutation journal directly, never '
          + 'from the board this page publishes.',
          [{ kind: 'activity' }], 'full', 'own feed'),
        D('hourly_nodes', 'Hourly reporting nodes — full day',
          "Every hourly pulse is visible. Completed nodes contain the CEO's source-linked "
          + 'note; future nodes remain waiting. This is the reporting spine DreamState reads.',
          [{ kind: 'hours' }], 'full', '24 nodes'),
        D('latest_ceo_notes', 'Latest CEO notes',
          'The CEO reads underlying calls, transcripts, admin actions, sales/marketing '
          + 'events and system telemetry, then writes a concise note in its own words.',
          [{ kind: 'timeline' }], 'full', 'source-linked'),
        D('dreamstate_patterns', 'DreamState pattern board',
          'DreamState works over reporting notes, transcripts and daily reports — never '
          + 'directly over operational Tier-1 doctrine.',
          [{ kind: 'table', columns: ['Pattern', 'Evidence', 'Owner action'] }], 'full', 'read-only'),
        D('daily_reports', 'Daily reports — archive and current draft',
          "Two reports per day, at the 07:00 and 19:00 gates, each compiled from the "
          + "CEO's hourly notes over the twelve hours behind it. Owners can open prior "
          + 'windows without reading every transcript individually.',
          [{ kind: 'reports' }], 'full', 'today draft'),
        D('gap_folders', 'Gap folders and owner-fill queue',
          'When T1 returns nothing, a T2 gap opens. When the owner fills T1, the active '
          + 'T2 gap folder resets.',
          [{ kind: 'table', columns: ['Gap', 'Agent', 'Entries', 'Current status', 'Action'] }], 'full'),
      ] },
      { key: 'plugin_operations', label: 'Plugin Operations', panels: [
        { ...D('ceo_plugin_banner', 'CEO Plugin Operations', undefined, [{
            kind: 'roleBanner',
            title: 'CEO Plugin Operations',
            text: 'Read-only operational oversight. The CEO watches telemetry, costs, '
              + 'health and audit surfaces without owning any of them.',
            pills: [
            { label: 'services healthy', data: 'services_healthy', valueFirst: true },
            { label: 'read-only' },
            { label: 'watches', data: 'watches', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        { ...D('service_ribbon', 'Service health', undefined, [{ kind: 'chips', items: [
            'Twilio Usage', 'Event Ledger', 'CRM / Job Analytics', 'Cost / Margin',
            'API watches', 'DGX Health', 'Audit', 'DreamState'] }], 'full'), chrome: true },
        D('twilio_usage', 'Twilio usage API — placeholder', 'Backend usage view across all business numbers.', [
          { kind: 'kpis', tiles: [
            { label: 'Minutes today', sub: 'all tenants' },
            { label: 'SMS segments' },
            { label: 'Number cost', sub: 'monthly DID estimate' } ] },
          { kind: 'table', backend: true, columns: ['Number / tenant', 'Calls', 'Minutes', 'Cost', 'Health'] },
        ], 'full', 'live sample'),
        D('event_ledger', 'Rexy event ledger — placeholder',
          'Latest immutable/tamper-evident operational events visible to the CEO.',
          [{ kind: 'ledger' }], 'full', 'source-linked'),
        D('cost_margin', 'Cost / margin dashboard', 'Per-tenant cost and package optimisation surface.',
          [{ kind: 'stats', items: ['Revenue', 'Transport', 'Models', 'Contribution'] },
           { kind: 'table', columns: ['Tenant', 'Package', 'Value', 'State'] }], 'full'),
        D('api_monitoring_queue', 'API monitoring queue',
          'Detected documentation/auth changes with affected connector and repair state.',
          [{ kind: 'table', backend: true, columns: ['Provider', 'Change', 'Priority'] }], 'full'),
        D('server_dgx_monitor', 'Server / DGX monitor', 'Owned runtime health, capacity and recovery state.',
          [{ kind: 'table', backend: true, columns: ['Service', 'State', 'Last check'] }], 'full'),
        D('crm_job_analytics', 'CRM / Job analytics — operational view',
          'Cross-tenant lead, booking, job and outcome signals. Read-only to the CEO.',
          [{ kind: 'stats', items: ['Qualified leads', 'Booked / jobs', 'Recovered calls', 'Escalated'] },
           { kind: 'table', backend: true, columns: ['Tenant', 'Outcome', 'Conversion', 'Signal'] }], 'full'),
        D('audit_compliance', 'Audit / compliance logs — operational view',
          'Owner-visible trace of sensitive reads, writes, transfers and approvals.',
          [{ kind: 'ledger' }], 'full'),
        D('ceo_connector_registry', 'CEO connector / API registry',
          'Read-only connector metadata belongs under the usage, ledger, cost, monitoring '
          + 'and server placeholders — not above them.',
          [{ kind: 'registry' }], 'full', 'read-only registry'),
      ] },
    ],
  },

  // ── 3 · MARKETING — 7 sub-tabs, 15 panels · BUCK ──────────────────────────
  // Transcribed from page-marketing in the design HTML: panel titles, sub-lines,
  // badges, table columns, KPI labels, metric names and checklists. Every figure
  // in that section is sample data and none of it is carried across.
  {
    key: 'marketing', label: 'Marketing', panels: [],
    note: 'Marketing finds, verifies, ranks and groups potential customers for the '
        + 'receptionist company. It does not contact prospects.',
    subTabs: [
      { key: 'campaign_overview', label: 'Campaign Overview', panels: [
        { ...D('campaign_kpis', 'Campaign totals', undefined, [{ kind: 'kpis', tiles: [
            { label: 'Discovered today', sub: 'all sectors' },
            { label: 'Verified', sub: 'business-owned sources' },
            { label: 'Qualified', sub: 'company fit passed' },
            { label: 'Ready for Sales' },
          ] }], 'full'), chrome: true },
        D('active_campaigns', 'Active campaigns',
          'Owners see the current search lanes, progress and quality — not raw scraping volume.',
          [{ kind: 'table', columns: ['Campaign', 'Region', 'Verified', 'Qualified', 'Sales-ready'] }]),
        D('marketing_fleet', 'Marketing fleet', 'Narrow workers feeding the Marketing Agent.', [
          { kind: 'pills', items: ['MAP-SWEEP', 'REGION-GRID', 'WEB-VERIFY', 'ABN-CHECK',
            'SCALE-SIGNAL', 'RECEPTION-CHECK', 'CONTACT-ROUTE', 'DUPLICATE-GATE',
            'FIT-JUDGE', 'SOURCE-KEEPER', 'SALES-HANDOFF'] },
          { kind: 'metrics', rows: ['Search lanes running', 'Source checks pending',
            'Duplicates merged', 'Owner-needed decisions'] },
        ]),
      ] },
      { key: 'regions_maps', label: 'Regions & Maps', panels: [
        D('maps_discovery', 'Google Maps / local discovery',
          'Sector + geography discovery. Each candidate is verified against its own website '
          + 'and other approved public evidence before qualification.',
          [{ kind: 'map' }], 'half', 'multi-sector campaign'),
        D('discovery_queries', 'Current discovery queries',
          'Split by sector and suburb cluster to avoid one broad, low-quality search.',
          [{ kind: 'table', columns: ['Query', 'Area', 'Found', 'Next'] }]),
        // A SECTION HEADER IN THE DESIGN, not a data panel — it introduces the grid
        // beneath it. Its own line is its content, so it is transcribed rather than
        // left as a bare heading.
        D('regional_coverage', 'Regional campaign coverage', undefined,
          [{ kind: 'note', text: 'Geography sits directly beneath Maps discovery: '
            + 'Australia → state → metro/region → suburb cluster → active sector.' }],
          'full', 'live coverage grid'),
        D('regional_grid', 'Regional campaign grid',
          'Australia → state → metro/region → suburb cluster → sector.',
          [{ kind: 'table', columns: ['State', 'Metro / region', 'Suburb cluster',
            'Active sector', 'Coverage'] }], 'full'),
      ] },
      { key: 'company_qualification', label: 'Company Qualification', panels: [
        D('qualification_board', 'Company qualification board',
          'Sector score is only the starting point. Each company is scored on its own operating evidence.',
          // FIT IS A PILL; THE COMPONENT SCORES BESIDE IT ARE NOT. In the design
          // every row's Pain/Reception gap/Scale/Phone/Evidence cell is plain text
          // and only the last cell is toned. WHICH column that is lives in
          // MEASURE_SLOTS beside its band, not here — one place says how a cell
          // is toned, so a rule cannot be added and silently not render.
          [{ kind: 'table', columns: ['Business', 'Pain', 'Reception gap', 'Scale',
            'Phone', 'Evidence', 'Fit'], lanes: true }]),
        D('qualification_signals', 'Qualification signals', undefined,
          [{ kind: 'checklist', items: [
            'Reception coverage / bottleneck', 'Hands-on or mobile work',
            'Phone-first booking or quote path', 'Urgency and substitution risk',
            'Staff / vans / bays / vets / crews / sites', 'Lead or booking value',
            'Existing systems and current receptionist solution',
            'Source freshness and confidence'] }]),
      ] },
      { key: 'internet_verification', label: 'Internet Verification', panels: [
        D('internet_verification', 'Internet verification',
          'Facts, inferences and unanswered questions are kept visibly separate.',
          // `Unknown (pain point)` IS THE NAME §5 GIVES IT, and this surface
          // claims to transcribe §5 verbatim. It said `Unknown`, which is the
          // column doing half its own job: the parenthetical is what tells the
          // reader the cell holds a PAIN POINT rather than a missing field.
          // Raised in scratchpad/unknown_column/EVIDENCE.md §5 as a front-end
          // item; this is that item.
          [{ kind: 'table', columns: ['Business', 'Verified facts', 'Inference', 'Unknown (pain point)',
            'Website?', 'Sources', 'Status'], lanes: true }], 'full'),
        D('website_opportunity', 'Website opportunity routing',
          'A simple secondary-opportunity check. Marketing does not scope or sell the website; '
          + 'it only flags whether a verified prospect has a usable website and routes the '
          + 'opportunity into the separate Website Domain for its own qualification.', [
            { kind: 'kpis', tiles: [
              { label: 'Verified prospects checked', sub: 'current campaigns' },
              { label: 'Website present', sub: 'review quality separately' },
              { label: 'No website found', sub: 'strong website-domain signal' },
              { label: 'Needs review', sub: 'redirect / social-only / stale' } ] },
            { kind: 'table', columns: ['Business', 'Website state', 'Receptionist opportunity',
              'Website opportunity', 'Route'] },
          ], 'full', 'secondary opportunity'),
      ] },
      { key: 'contact_routes', label: 'Contact Routes', panels: [
        D('public_contact_routes', 'Public contact routes',
          'Company-level routes only. Contact discovery is not permission to send marketing.',
          [{ kind: 'table', columns: ['Business', 'Buyer role', 'Main phone',
            'Business email / form', 'Source', 'Confidence'], lanes: true }], 'full'),
      ] },
      { key: 'campaign_lists', label: 'Campaign Lists', panels: [
        D('qualified_lists', 'Qualified campaign lists',
          'Only verified, scored businesses enter these lists. The batch target is a quality '
          + 'gate, not a quota to pad.',
          [{ kind: 'table', columns: ['Campaign', 'Qualified list', 'Rejected / watchlist',
            'Next action'],
            action: { column: 'Next action', when: 'handoff_readiness' } }], 'full'),
      ] },
      // ── OWNER RULING: THE SALES HANDOFF TAB IS TAKEN DOWN ENTIRELY ──────
      // Campaign summary, Sales-ready evidence and Company handoff list are gone
      // — all three, not folded in beside the workbook. The workbook IS the
      // handoff boundary now: Marketing writes into the file, Sales works it.
      //
      // NEITHER THE KEY NOR THE LABEL MOVES. `sales_handoff` is IDENTITY — the
      // round-trip acceptance navigates to it by key — and identity never renames
      // for a change of contents. The LABEL stays "Sales Handoff" by owner ruling
      // (2026-08-20): I renamed it to match the panel beneath and it was approved,
      // and the owner then reverted both. The tab is what the domain calls this
      // step of its work; the workbook is how the step is done. The role banner
      // inside the tab already names the surface, so nothing is unlabelled.
      { key: 'sales_handoff', label: 'Sales Handoff',
        panels: SALES_WORKBOOK_PANELS },
    ],
  },

  // ── 4 · SALES — 10 sub-tabs, 51 panels · DELTA. The largest surface. ──────
  // Transcribed from page-sales. Sales carries shapes no other root has: the
  // six-sheet workbook, the pain chain as a flow, the script sheet on a LIGHT
  // background, the 16:9 slide grid, the funnel and the decay tracker.
  //
  // SALES PREPARES. THE OWNER CALLS, APPROVES, SENDS, SIGNS AND CHARGES — which
  // is why nothing on this root has a send, sign or charge control.
  {
    key: 'sales', label: 'Sales', panels: [],
    note: 'Sales prepares. The owner calls, approves, sends, signs and charges. The '
        + 'detailed company and sector records live in the terminal-backed workbook.',
    subTabs: [
      { key: 'sales_overview', label: 'Sales Overview', panels: [
        { ...D('sales_kpis', 'Sales totals', undefined, [{ kind: 'kpis', tiles: [
            // READY FOR SALES, NOT QUALIFIED (owner ruling 2026-09-17): the
            // handoff is every ready campaign's qualified list. The producer's
            // key is `core/delta_board.sales_kpis` — renamed in the same change.
            { label: 'Ready for Sales', sub: 'from Marketing' },
            { label: 'Research complete', sub: 'sector + company overlay' },
            { label: 'Cold calls due', sub: 'split across 2 owners' },
            // ⭐⭐ THE WORK THAT IS NOT ON TODAY'S LIST (owner ruling 2026-09-21).
            // Every tile above is counted over the handoff, so a script written
            // for a company Buck no longer hands over was counted nowhere: the
            // screen read 38 while the store held 97. Its value comes from the
            // same producer, keyed on this label.
            { label: 'Scripted, not handed', sub: 'written, not on today\'s handoff' },
            { label: 'Consultations booked', sub: '30-minute pitch format' },
            { label: 'Decay / escalation' },
          ] }], 'full'), chrome: true },
        D('sales_operating_path', 'Sales operating path',
          'Overview is a reporting dashboard. The detailed company and sector records '
          + 'live in the terminal-backed Excel workbook.',
          [{ kind: 'flow', steps: [
            { label: '1. Receive', sub: 'Marketing handoff' },
            { label: '2. Workbook', sub: 'companies + sectors' },
            { label: '3. Diagnose', sub: 'research + pain + buyer' },
            { label: '4. Call', sub: 'objections + pivots' },
            { label: '5. Pitch', sub: 'consultation + deck' },
            { label: '6. Progress', sub: 'CRM + follow-up + close' } ] }], 'full', 'owner-led sales'),
        D('sales_funnel', 'Sales funnel and owner workload',
          'Workbook records roll up here without turning the overview into a second spreadsheet.',
          [{ kind: 'funnel', stages: ['Incoming from Marketing', 'Research complete',
            'Cold call ready', 'Consultation booked', 'Pilot / proposal', 'Won'] }], 'half', 'live rollup'),
        D('owners_today', 'Owners today', 'Calls and consultations allocated to the two owner users.',
          [{ kind: 'metrics', rows: ['Calls completed', 'Consultations prepared', 'Follow-ups overdue'] }], 'half'),
        D('active_prospect_work', 'Active prospect work',
          'Current state, next owner action and decay visibility.',
          [{ kind: 'table', columns: ['Company', 'Sector', 'State', 'Owner', 'Next action'] }], 'full'),
        D('sales_fleet', 'Sales fleet', 'Narrow workers underneath the Sales Agent.', [
          { kind: 'pills', items: ['WORKBOOK-SYNC', 'PACKET-VALIDATE', 'SECTOR-PAIN',
            'COMPANY-DEEP-RESEARCH', 'BUYER-MAP', 'OBJECTION-BRANCH', 'CALL-TRACKER',
            'SCRIPT-WRITER', 'PPTX-BUILDER', 'DECAY-WATCH', 'SOURCE-KEEPER'] },
          { kind: 'metrics', rows: ['Workbook rows', 'Research runs today', 'Scripts ready', 'Decks ready'] },
        ], 'full'),
      ] },

      // THE SAME SURFACE AS MARKETING'S, FROM THE SAME DECLARATION — not a copy.
      // One file, two tabs: what Marketing writes at the handoff boundary is what
      // Sales opens here, because it is the same workbook.
      { key: 'companies_sector_workbook', label: 'Companies + Sector Workbook',
        panels: SALES_WORKBOOK_PANELS },

      { key: 'company_research_diagnosis', label: 'Company Research & Diagnosis', panels: [
        { ...D('sales_diagnosis_banner', 'Company Research & Diagnosis — {tenant}', undefined, [{
            kind: 'roleBanner',
            title: 'Company Research & Diagnosis — {tenant}',
            text: 'Company research, sector pain, company-specific pain and buyer '
              + 'discovery are one continuous scroll so the diagnosis remains '
              + 'connected.',
            pills: [
            { label: 'research complete' },
            { label: 'sources', data: 'sources_checked', valueFirst: true },
            { label: 'assumptions to confirm', data: 'assumptions_to_confirm', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        D('company_profile', 'Company profile', undefined,
          [{ kind: 'metrics', rows: ['Service model', 'Scale', 'Service mix', 'Coverage'] }], 'half'),
        D('systems_clues', 'Systems / workflow clues', undefined,
          [{ kind: 'metrics', rows: ['Booking', 'Dispatch', 'CRM', 'Payments'] }], 'half'),
        D('why_now', 'Why now', undefined,
          [{ kind: 'metrics', rows: ['Hiring', 'Hours', 'Reviews', 'Website CTA'] }], 'half'),
        D('research_evidence_ledger', 'Research evidence ledger',
          'Facts, hypotheses and unanswered questions stay separate.',
          [{ kind: 'table', columns: ['Finding', 'State', 'Source', 'Confidence', 'Sales use'] }], 'half'),
        D('sector_pain', 'Sector pain', undefined,
          [{ kind: 'metrics', rows: ['Urgency', 'Substitution', 'Hands-busy state', 'General risk'] }], 'half'),
        D('company_pain', 'Company pain', undefined,
          [{ kind: 'metrics', rows: ['Scale', 'Hours', 'Service breadth', 'Gap'] }], 'half'),
        D('pain_chain', 'Company-specific pain chain',
          'Evidence-backed where possible; unknowns become questions.',
          [{ kind: 'flow', steps: [
            { label: 'Caller locked out', sub: 'high intent' },
            { label: 'All vans active', sub: 'likely scenario' },
            { label: 'Call unanswered', sub: 'must confirm' },
            { label: 'Caller substitutes', sub: 'sector pattern' },
            { label: 'Job lost', sub: 'commercial consequence' },
            { label: 'Rexy captures', sub: 'proposed future state' } ] }], 'full'),
        D('cost_of_inaction', 'Cost of inaction — defensible', undefined,
          [{ kind: 'metrics', rows: ['Delayed callback', 'Incomplete job data',
            'Unsafe driving interruption', 'Exact lost revenue'] }], 'half'),
        D('rexy_fit', 'Rexy fit', undefined,
          [{ kind: 'metrics', rows: ['Answer immediately', 'Capture job context',
            'Account/site continuity', 'Authorise entry'] }], 'half'),
        D('likely_buyer', 'Likely buyer', undefined,
          [{ kind: 'metrics', rows: ['Primary', 'Secondary', 'Current route', 'Authority confidence'] }], 'half'),
        D('discovery_questions', 'Prioritised discovery questions',
          'Each question tests a specific assumption and changes the proposal.',
          [{ kind: 'questions', items: [
            { q: 'Who answers after hours when every technician is active?', tests: 'Tests the actual reception gap.', tag: 'highest value' },
            { q: 'How many calls are returned too late or never leave a usable message?', tests: 'Builds the measurable baseline.', tag: 'baseline' },
            { q: 'What information must be collected before a technician can accept a job?', tests: 'Defines the intake workflow.', tag: 'workflow' },
            { q: 'How do you verify authority before attending or opening a property?', tests: 'Defines the human/security boundary.', tag: 'safety' },
            { q: 'Which jobs can receive an indicative range and which require inspection?', tests: 'Defines quote authority.', tag: 'commercial' },
            { q: 'How are repeat commercial sites and property managers handled?', tests: 'Tests continuity value.', tag: 'memory' } ] }], 'full'),
      ] },

      { key: 'calls_objections_pivots', label: 'Calls, Objections & Pivots', panels: [
        { ...D('sales_calls_banner', 'Calls, Objections & Pivots', undefined, [{
            kind: 'roleBanner',
            title: 'Calls, Objections & Pivots',
            text: 'One operational tab holds the owner call queue, live script, '
              + 'completed-call tracker and every prepared objection pivot.',
            pills: [
            { label: 'calls today', data: 'calls_today', valueFirst: true },
            { label: 'owner queues', data: 'owner_queues', valueFirst: true },
            { label: 'objection gaps', data: 'objection_gaps', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        D('cold_call_tracker', 'Cold-call tracker — today',
          'Checks sync into the Sales Calendar and CRM activity record.',
          [{ kind: 'tasks' }], 'half'),
        D('call_outcomes', 'Call outcomes today', 'Each completion writes the outcome and next action.',
          [{ kind: 'table', columns: ['Company', 'Reached', 'Pain', 'Next action'] }], 'half'),
        D('cold_call_script', 'Cold-call script',
          'Goal: earn a consultation, not explain the entire platform.',
          [{ kind: 'script' }], 'full'),
        // ⭐⭐ THE NINE OBSERVABLE CLASSES (owner ruling 2026-09-16). The six
        //    that were here — Interested, Already covered, AI objection, Price
        //    immediately, Send information, Unknown curveball — were the mock's
        //    own fill: `Interested` is not an objection at all and `AI objection`
        //    collapsed two. These are the classes `Objection Handling and
        //    Pivots` §5 Step 2 declares and Delta's branches already carry.
        //    Reliability and Operational risk are TWO rows on purpose: "it'll
        //    get things wrong" and "what happens if it goes down" are different
        //    objections with different answers, written as B5 and B6.
        //    A tenth — explicit refusal — is owed and its manual is not written.
        // ⭐⭐⭐ `Not counted` (owner ruling 2026-09-17). A branch whose manual no
        //    row owns is reported by the producer under this label — "B4
        //    commercial" — and never guessed into a row. Without the row the
        //    panel drew nine and the branch vanished: "A BRANCH THAT VANISHES
        //    FROM THE COUNTER IS A BRANCH THE OWNER THINKS WAS NEVER WRITTEN."
        //    It is not a tenth class; a script whose branches all place shows a dash.
        D('call_branches', 'Call branches', undefined,
          [{ kind: 'metrics', rows: ['Timing', 'Information / deferral', 'Existing coverage',
            'Commercial', 'Reliability', 'Operational risk', 'Fit / customisation',
            'Privacy / trust', 'Prior negative experience', 'Not counted'] }], 'full'),
      ] },

      { key: 'consultation_pitch', label: 'Consultation & Pitch', panels: [
        { ...D('sales_pitch_banner', 'Consultation & Pitch — {tenant}', undefined, [{
            kind: 'roleBanner',
            title: 'Consultation & Pitch — {tenant}',
            text: 'The 30-minute consultation, Word talk track and editable '
              + 'PowerPoint workspace now live together.',
            pills: [
            { label: 'script', data: 'script_version' },
            { label: 'slide deck', data: 'slide_count', valueFirst: true },
            { label: 'owner approval' },
            ],
          }], 'full'), chrome: true },
        D('consultation_agenda', '30-minute consultation agenda',
          'Cold call earns this meeting; the pitch diagnoses before it demonstrates.',
          [{ kind: 'table', columns: ['Time', 'Block', 'Purpose'] }], 'half', 'screen-share'),
        D('consultation_controls', 'Consultation controls', undefined,
          [{ kind: 'metrics', rows: ['Decision-makers invited', 'Demo scope', 'Systems to confirm',
            'Safety boundary', 'Measurement baseline', 'Recommended next step'] }], 'half'),
        D('sales_copilot', 'Sales Agent co-pilot',
          'Help the owners shape the deck without sending or presenting autonomously.',
          [{ kind: 'suggestions' }], 'half'),
        D('sales_call_brief', 'Word SalesCallBrief — owner talk track',
          'The full brief remains available beside the deck during the consultation.',
          [{ kind: 'script' }], 'full', 'v3 ready'),
        D('opening', 'Opening', undefined, [{ kind: 'script' }], 'half'),
        D('five_key_questions', 'Five key questions', undefined,
          [{ kind: 'checklist', items: [
            'Who answers when every technician is active?',
            'How is authority verified?',
            'Which details are required before dispatch?',
            'Which jobs can receive an indicative range?',
            'How are repeat commercial sites managed?'] }], 'half'),
        D('recommended_next_step', 'Recommended next step',
          'Map one after-hours inbound flow and run a narrow measurable pilot.',
          [{ kind: 'slides', count: 12 }], 'full'),
        D('live_consultation_notes', 'Live consultation notes',
          'Structured fields feed the outcome record and follow-up decay.',
          [{ kind: 'table', columns: ['Area', 'Placeholder note', 'State'] }], 'full'),
      ] },

      { key: 'crm_pipeline', label: 'CRM / Pipeline', panels: [
        { ...D('sales_pipeline_banner', 'HubSpot CRM — Sales pipeline', undefined, [{
            kind: 'roleBanner',
            title: 'HubSpot CRM — Sales pipeline',
            text: 'Incoming companies, owner activities, consultations, commercial '
              + 'stages and outcomes.',
            pills: [
            { label: 'connected' },
            ],
          }], 'full'), chrome: true },
        D('pipeline_value', 'Pipeline value', undefined,
          [{ kind: 'metrics', rows: ['Qualified', 'Consultation', 'Pilot / proposal'] }], 'half'),
        D('owner_activity', 'Owner activity', undefined,
          [{ kind: 'metrics', rows: ['Owner 1 open tasks', 'Owner 2 open tasks', 'Unassigned'] }], 'half'),
        D('crm_sync_state', 'CRM sync state', undefined,
          [{ kind: 'metrics', rows: ['Workbook → HubSpot', 'Calendar activities', 'Duplicate gate'] }], 'half'),
      ] },

      { key: 'inbox_sales_calendar', label: 'Inbox + Sales Calendar', panels: [
        { ...D('sales_comms_banner', 'Sales Inbox + Sales Calendar', undefined, [{
            kind: 'roleBanner',
            title: 'Sales Inbox + Sales Calendar',
            text: "This is the Sales Agent's own workspace: prospect replies, "
              + 'cold-call windows, consultations, follow-ups and decay escalation.',
            pills: [
            { label: 'mail healthy' },
            { label: 'calendar healthy' },
            ],
          }], 'full'), chrome: true },
        D('sales_inbox', 'Gmail / Outlook — Sales inbox',
          'Drafts and replies are visible; sends remain approval-gated.',
          [{ kind: 'inbox' }], 'full'),
        D('cold_call_checklist', 'Cold-call checklist',
          'The same tasks appear in Calls and on the calendar.', [{ kind: 'tasks' }], 'half', 'today'),
        D('sales_calendar', 'Google / Microsoft Calendar — Sales only',
          'Cold-call windows, consultations and agreed follow-ups. Receptionist booking '
          + 'calendars remain separate.', [{ kind: 'calendar' }], 'full'),
      ] },

      { key: 'commercials', label: 'Commercials', panels: [
        D('docusign', 'DocuSign', 'Agreement state after owner approval.',
          [{ kind: 'metrics', rows: ['Draft envelopes', 'Awaiting signature', 'Signed this month'] }], 'half'),
        D('xero', 'Xero', 'Setup invoice and account status.',
          [{ kind: 'metrics', rows: ['Draft setup invoice', 'Retainer', 'Invoice state'] }], 'half'),
        D('stripe', 'Stripe', 'Payment links and status.',
          [{ kind: 'metrics', rows: ['Payment links active', 'Paid today', 'Failed'] }], 'half'),
        D('sector_integration_intelligence', 'Sector integration intelligence',
          'Patterns help Sales pitch relevant integrations. Every status is either verified '
          + 'or observed.',
          [{ kind: 'table', columns: ['Sector', 'Primary operational system', 'Accounting',
            'Payments', 'E-sign', 'CRM', 'Calendar / dispatch', 'Sales use'] }], 'full', 'pitch intelligence'),
        D('company_stack_tracker', 'Company stack tracker',
          'Company-specific evidence overrides sector patterns.',
          [{ kind: 'table', columns: ['Company', 'Sector', 'Verified systems',
            'Inferred / unknown', 'Pitch integration angle'] }], 'full'),
      ] },

      { key: 'competitive_pulse', label: 'Competitive Pulse', panels: [
        { ...D('sales_pulse_banner', 'Competitive Pulse + Follow-up Decay', undefined, [{
            kind: 'roleBanner',
            title: 'Competitive Pulse + Follow-up Decay',
            text: 'Two separate rhythms: sector crowding refreshes every 14 days; '
              + 'each consultation enters a 15-day decay ladder.',
            pills: [
            { label: 'market pulse', data: 'market_pulse' },
            { label: 'next', data: 'next_pulse' },
            { label: 'CEO escalation', data: 'ceo_escalation', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        D('competitive_signals', 'New competitive signals', undefined,
          [{ kind: 'table', columns: ['Provider', 'Change', 'Sector', 'Sales implication'] }], 'full'),
        D('rexy_emphasis', 'Current Rexy emphasis', undefined,
          [{ kind: 'metrics', rows: ['Crowded sectors', 'Whitespace sectors', 'Memory claim',
            'Model claim'] }], 'half'),
        D('decay_tracker', 'Active consultation decay tracker',
          'Decay score, missed pulses and next touch are visible to Sales and the Domain CEO.',
          [{ kind: 'table', columns: ['Company', 'Consultation', 'Day', 'Decay score',
            'Next touch', 'Missed pulses', 'Escalation'] }], 'full', 'CEO threshold: day 15 or 3 missed pulses'),
        D('ceo_escalation_queue', 'CEO escalation queue',
          'Only unresolved or repeatedly missed follow-ups surface to the reporting harness.',
          [{ kind: 'timeline' }], 'half'),
        D('decay_controls', 'Decay controls', undefined,
          [{ kind: 'metrics', rows: ['Day 2', 'Day 5', 'Day 10', 'Day 15', 'After owner decision'] }], 'half'),
      ] },

      { key: 'plugin_operations', label: 'Plugin Operations', panels: [
        { ...D('sales_plugin_banner', 'Sales Plugin Operations', undefined, [{
            kind: 'roleBanner',
            title: 'Sales Plugin Operations',
            text: 'Operational placeholders first; connector metadata and setup '
              + 'sources underneath.',
            pills: [
            { label: 'core services healthy', data: 'core_services_healthy', valueFirst: true },
            { label: 'optional', data: 'optional_services', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        D('sales_workbook_plugin', 'Sales workbook', 'Internal XLSX engine and interface mirror.',
          [{ kind: 'metrics', rows: ['Rows', 'Sheets', 'Last sync'] }], 'half'),
        D('research_lanes', 'Research lanes', 'OpenAI primary; Claude cross-check.',
          [{ kind: 'metrics', rows: ['Runs today', 'Sources captured', 'Cross-checks'] }], 'half'),
        D('document_generation', 'Document generation', 'Internal Word and PowerPoint.',
          [{ kind: 'metrics', rows: ['DOCX ready', 'PPTX ready', 'Owner approval'] }], 'half'),
        D('commercial_connectors', 'Commercial connectors', 'Gated after human decision.',
          [{ kind: 'metrics', rows: ['DocuSign', 'Xero', 'Stripe'] }], 'half'),
        D('sales_connector_registry', 'Sales connector / setup registry',
          'Exact source location for implementation. Internal Rexy modules remain owned.',
          [{ kind: 'table', columns: ['Priority', 'Plugin', 'Main job', 'Best app location',
            'Best source / setup'] }], 'full', 'launch map'),
      ] },
    ],
  },

  // ── 5 · COMPANY AGENTS — 6 sub-tabs, 11 panels · PARAMETERISED ───────────
  // ONE SYSTEM, SEVEN TENANTS, NOT SEVEN BUILDS. The tenant is a route parameter;
  // the sub-tabs and panels below are IDENTICAL for every tenant. The sixth tab's
  // LABEL carries the selected tenant's admin code, which is why it is templated
  // in MissionControl and not written as a literal here — no tenant is named.
  //
  // THE HARD BOUNDARY IS IN THE UI, NOT ONLY IN THE PROSE: caller waiting →
  // Reception's DIRECT live-call plugins. Call finished → the attached Admin does
  // the post-call work. There is no live Reception → Admin → plugin relay while a
  // caller waits, and no control on this root offers one.
  {
    key: 'company_agents', label: 'Company Agents', panels: [],
    note: 'One parameterised reception system. While a caller waits, Reception uses its '
        + 'direct live-call plugins; once the call finishes, the attached Admin Agent '
        + 'performs the post-call work. No live relay runs while a caller is waiting.',
    subTabs: [
      { key: 'reception_landing', label: 'Reception Landing', panels: [
        D('package_signal', 'Current package signal',
          'DreamState CEO watches call length and sector shape to recommend tiers.',
          [{ kind: 'metrics', rows: ['Monthly minutes projection', 'Suggested tier', 'Budget risk',
            'Voice minutes today', 'Messages', 'Margin watch'] }], 'full', 'Intermediate'),
      ] },
      // RECEPTION PLUGINS ARE THE LIVE-CALL SIDE OF THE HARD BOUNDARY. Every panel
      // here is reachable WHILE A CALLER WAITS, which is why the registry sits at
      // the BOTTOM: connector metadata belongs below the operational surfaces, and
      // an operator on a live call must not have to scroll past configuration.
      { key: 'reception_plugins', label: 'Reception Plugins', panels: [
        { ...D('reception_plugin_banner', '{tenant} Reception Plugin Operations', undefined, [{
            kind: 'roleBanner',
            title: '{tenant} Reception Plugin Operations',
            text: 'Operational plugin surfaces used directly during the live call. '
              + 'Owners see the work first; connector/API metadata sits at the '
              + 'bottom.',
            pills: [
            { label: 'live-call path' },
            { label: 'plugins', data: 'plugin_count', valueFirst: true },
            ],
          }], 'full'), chrome: true },
        D('twilio_live_console', 'Twilio Voice / Conversation Relay — live console',
          'Actual call path surface: active calls, turn latency, barge-in and transfer readiness.',
          [{ kind: 'callTiles', tiles: [
            // THE SUB IS THE DESIGN'S OWN LINE. The first is tenant-parameterised
            // and fillTenant already covers it — one system, seven tenants.
            { label: 'Active calls',         sub: '{tenant} number' },
            { label: 'First audio response', sub: 'under 2s target' },
            { label: 'Barge-ins today',      sub: 'recovered cleanly' },
            { label: 'Transfers',            sub: '100% context packet' },
          ] },
           { kind: 'messages' }],
          'full', 'live'),
        D('sector_operational_record', 'Sector system — live operational record',
          'The company\u2019s own operational record, used directly during the call.',
          [{ kind: 'table', columns: ['Record', 'Field', 'Value', 'Source'] }], 'half'),
        D('live_schedule', 'Schedule — availability and dispatch',
          'Live availability / dispatch / bay state. Health is the green light only.',
          [{ kind: 'calendar' }], 'half'),
        D('stripe_funding_state', 'Stripe — payment / funding state',
          'Live payment state for the current caller without exposing full sensitive credentials.',
          [{ kind: 'metrics', rows: ['Payment method on file', 'Outstanding', 'Last payment',
            'Link state'] }], 'half'),
        D('crm_lookup', 'Customer / CRM lookup',
          'Caller identity, continuity and current customer record.',
          [{ kind: 'metrics', rows: ['Matched record', 'Confidence', 'Last contact',
            'Open items'] }], 'half'),
        D('human_transfer', 'Human transfer / escalation',
          'Warm/cold transfer destinations and current readiness.',
          [{ kind: 'table', columns: ['Destination', 'Type', 'Readiness'] }], 'half'),
        D('p2_finance_inventory', 'P2 finance / inventory visibility',
          'Expansion connectors appear as real backend placeholders when enabled.',
          [{ kind: 'cards', empty: {
            name: 'No P2 connectors',
            detail: 'Current sector launch stack is P1-only.' } }], 'half'),
        D('live_transaction_log', 'Live plugin transaction log',
          'Every read/write stays source-linked for owner review.',
          [{ kind: 'table', backend: true, columns: ['Time', 'Plugin', 'Action', 'Result', 'Latency'] }], 'full'),
        D('reception_registry', 'Reception connector / API registry',
          'Backend connection health, permissions and system-of-record metadata. Kept below '
          + 'the operational placeholders.',
          [{ kind: 'plugins' }], 'full', 'registry only'),
      ] },
      { key: 'identity', label: 'Identity', panels: [
        D('caller_identification', 'Caller identification review', undefined,
          [{ kind: 'table', columns: ['Caller', 'Matched record', 'Phone', 'Email',
            'Confidence', 'Status'] }], 'full'),
        D('identity_activity', 'Identity activity',
          'Operational checks and exceptions for the selected receptionist.',
          [{ kind: 'metrics', rows: ['Known callers today', 'New callers',
            'Multiple-match reviews', 'Unable to verify', 'Phone numbers normalised'] }], 'full'),
      ] },
      { key: 'booking', label: 'Booking', panels: [
        { ...D('reception_booking_banner', 'Booking + payment operations', undefined, [{
            kind: 'roleBanner',
            title: 'Booking + payment operations',
            text: 'Calendar, appointment management and funding/payment state are '
              + 'monitored in one place.',
            pills: [
            { label: 'calendar live' },
            { label: 'payments live' },
            ],
          }], 'full'), chrome: true },
        D('calendar_today', 'Calendar — today', 'Live booking placeholder for the selected business.',
          [{ kind: 'calendar' }], 'full'),
        D('appointment_management', 'Appointment management',
          'Booking, reschedule, cancellation and conflict state.',
          [{ kind: 'table', columns: ['Capability', 'Today', 'Status'] }], 'half'),
        D('stripe_payment_state', 'Stripe — payment state',
          'Usual payment procedure, payment links and outstanding amounts.',
          [{ kind: 'table', backend: true, columns: ['Customer', 'Booking', 'Method', 'State'] }], 'half'),
        D('xero_customer_view', 'Xero / accounting — customer account view',
          'Invoice and account placeholder associated with booked work.',
          [{ kind: 'table', backend: true, columns: ['Ref', 'Customer', 'Amount', 'Status'] }], 'full'),
      ] },
      { key: 'transcripts', label: 'Transcripts', panels: [
        { ...D('reception_transcripts_banner', 'Call transcripts', undefined, [{
            kind: 'roleBanner',
            title: 'Call transcripts',
            text: 'Every call retains an owner-visible full transcript placeholder, '
              + 'outcome and linked post-call work.',
            pills: [
            { label: 'calls today', data: 'calls_today', valueFirst: true },
            { label: 'full transcript view' },
            ],
          }], 'full'), chrome: true },
        D('recent_calls', 'Recent calls', 'Open any call to review the entire conversation.',
          [{ kind: 'transcripts' }], 'full'),
      ] },
      // ── THE ATTACHED ADMIN AGENT — AN AGENT, WITH ITS OWN THREE VIEWS ───────
      // The HTML nests a switcher (switchAdminView) inside this sub-tab: Admin
      // Overview · Admin Plugin Operations · Connector Registry. That nesting is
      // reproduced, not flattened — flattening it is what previously left the
      // Admin Agent looking like two panels bolted onto someone else's root.
      //
      // EVERY PANEL BELOW IS POST-CALL. The attached Admin never touches a live
      // call: it reads the finished transcript and performs the approved
      // background work. That is the other half of the hard boundary.
      { key: 'admin_agent', label: 'Admin Agent', panels: [], views: [
        { key: 'admin_overview', label: 'Admin Overview', panels: [
        { ...D('admin_agent_banner', '{tenant}-A Admin Agent', undefined, [{
            kind: 'roleBanner',
            title: '{tenant}-A Admin Agent',
            text: 'Dedicated one-to-one post-call agent attached to the selected '
              + "receptionist. It reads only that company's transcript, queues and "
              + 'approved tools.',
            pills: [
            { label: 'paired' },
            { label: '{tenant}-A' },
            ],
          }], 'full'), chrome: true },
          { ...D('admin_kpis', 'Admin totals', undefined, [{ kind: 'kpis', tiles: [
              { label: 'Tasks today', sub: 'post-call only' },
              { label: 'Emails', sub: 'confirmations + payment' },
              { label: 'SMS', sub: 'retries tracked' },
              { label: 'Open follow-ups', sub: 'decay scheduler' },
            ] }], 'full'), chrome: true },
          D('transcript_to_admin_queue', 'Transcript-to-admin queue',
            'Every task links back to the originating receptionist call.',
            [{ kind: 'table', columns: ['Call', 'Task', 'Channel', 'Status'] }], 'half'),
          D('admin_agent_fleet', 'Admin fleet',
            'Narrow workers underneath this company-specific Admin Agent.',
            [{ kind: 'pills', items: ['TRANSCRIPT-READER', 'EMAIL-SENDER', 'SMS-SENDER',
              'PAYMENT-REQUEST', 'DECAY-SCHEDULER', 'CRM-SYNC', 'ESCALATION-WATCH',
              'AUDIT-DEPOSIT'] }], 'half'),
        ] },
        { key: 'admin_workspace', label: 'Admin Plugin Operations', panels: [
        { ...D('admin_plugins_banner', '{tenant}-A Admin Plugins', undefined, [{
            kind: 'roleBanner',
            title: '{tenant}-A Admin Plugins',
            text: 'Connector/API registry only. The actual inbox, messages, sector '
              + 'system, CRM, follow-up, payments and accounting surfaces live in '
              + 'Plugin Workspace.',
            pills: [
            { label: 'post-call' },
            { label: 'plugins', data: 'plugin_count', valueFirst: true },
            ],
          }], 'full'), chrome: true },
          D('admin_inbox', 'Gmail / Outlook inbox',
            'Every post-call email for this company is visible here.',
            [{ kind: 'inbox' }], 'full'),
          D('admin_sms_centre', 'Twilio SMS — message centre',
            'Confirmations, reminders, ETA/payment messages and delivery state.',
            [{ kind: 'table', columns: ['Time', 'Recipient', 'Message', 'Delivery'] }], 'half'),
          D('admin_sector_postcall', 'Sector system — post-call operations',
            'Admin reads the transcript and performs the approved background update.',
            [{ kind: 'table', columns: ['Call', 'Record', 'Update', 'State'] }], 'half'),
          D('admin_crm_update', 'Customer / CRM Update',
            'Post-call customer continuity, preferences and unresolved issues.',
            [{ kind: 'table', columns: ['Customer', 'Change', 'Source call', 'State'] }], 'half'),
          D('admin_follow_up_scheduler', 'Follow-up scheduler',
            'Admin reminders, callbacks, payment chases and scheduled messages.',
            [{ kind: 'table', columns: ['Due', 'Task', 'Channel', 'Decay day', 'State'] }], 'half'),
          D('admin_payment_requests', 'Stripe — payment requests',
            'Payment links, receipts, failed-payment follow-up and source-call linkage.',
            [{ kind: 'table', columns: ['Customer', 'Amount', 'Link', 'Source call', 'State'] }], 'half'),
          D('admin_accounting', 'Xero / MYOB — company accounting',
            'Invoices and accounts generated or followed up by this Admin Agent.',
            [{ kind: 'table', columns: ['Ref', 'Customer', 'Amount', 'Status'] }], 'half'),
          D('admin_delivery_watch', 'Admin delivery / escalation watch',
            'Delivery failures, retries and human-owner escalation.',
            [{ kind: 'table', columns: ['Task', 'Attempts', 'Last error', 'Escalation'] }], 'full'),
        ] },
        { key: 'admin_registry', label: 'Connector Registry', panels: [
          { ...D('admin_registry_kpis', 'Registry totals', undefined, [{ kind: 'kpis', tiles: [
              { label: 'Attached admins', sub: '1 per receptionist slot' },
              { label: 'Post-call tasks' },
              { label: 'Decay follow-ups' },
              { label: 'Transcript reads', sub: 'source-linked' },
            ] }], 'full'), chrome: true },
          D('admin_pairing_view', 'Receptionist ↔ Admin pairing',
            'Backend isolation and workload by company.',
            [{ kind: 'table', columns: ['Receptionist', 'Attached admin', 'Sector', 'Plugins',
              'Queue', 'Status'] }], 'full'),
          D('admin_registry_grid', 'Admin connector / API registry',
            'Connector metadata for this company-local Admin Agent. Kept below the '
            + 'operational surfaces, never above them.',
            [{ kind: 'plugins' }], 'full', 'registry only'),
        ] },
      ] },
    ],
  },

  // ── 6 · ADMIN FLEET — no sub-tabs · ECHO ─────────────────────────────────
  // THE HTML CARRIES SIX PANELS HERE, NOT THREE. The spec's "3" counted only the
  // first row; page-admin also carries the execution loop, its runtime counters
  // and the knowledge-gap queue. The HTML is the acceptance, so all six are built.
  //
  // Read-only cross-tenant roll-up. There is NO single shared Admin Agent — each
  // receptionist has its own, and this root reports across them.
  {
    key: 'admin_fleet', label: 'Admin Fleet', subTabs: [],
    note: 'Read-only cross-tenant roll-up across every company-local Admin Agent. '
        + 'There is no single shared Admin Agent. Lifecycle: Open → Due → Retry → '
        + 'Escalate to human owner.',
    panels: [
      D('receptionist_admin_pairing', 'Receptionist ↔ Admin pairing',
        'Backend isolation and workload by company.',
        [{ kind: 'table', backend: true, columns: ['Receptionist', 'Attached admin', 'Sector', 'Plugins',
          'Queue', 'Status'] }], 'full'),
      D('admin_fleet_workers', 'Admin fleet workers',
        'Each attached admin receives the same worker classes but keeps tenant-local state.',
        [{ kind: 'pills', items: ['TRANSCRIPT-READER', 'EMAIL-SENDER', 'SMS-SENDER',
          'PAYMENT-REQUEST', 'DECAY-SCHEDULER', 'CRM-SYNC', 'ESCALATION-WATCH',
          'AUDIT-LOGGER'] }], 'half'),
      D('follow_up_decay_rollup', 'Follow-up decay rollup',
        'Deterministic follow-up state across all company-local admin agents.',
        [{ kind: 'flow', steps: [
          { label: 'Open', sub: 'source call' },
          { label: 'Due', sub: 'time window' },
          { label: 'Retry', sub: 'if failed' },
          { label: 'Escalate', sub: 'human owner' } ] }], 'half'),
      D('execution_loop_health', 'Execution loop health',
        'Same bounded runtime across Marketing, Sales, Reception, Admin and fleet agents.',
        [{ kind: 'flow', steps: [
          { label: 'Ground', sub: 'required context available' },
          { label: 'Draft', sub: 'first pass' },
          { label: 'Hard checks', sub: 'deterministic' },
          { label: 'Evaluate', sub: 'second check' },
          { label: 'Repair', sub: 'only if needed' },
          { label: 'Complete', sub: 'or honest failure' } ] }], 'full'),
      D('runtime_counters', 'Runtime counters today', undefined,
        [{ kind: 'metrics', rows: ['Completed first pass', 'Repair rounds', 'Honest failures',
          'Owner-needed knowledge gaps', 'Maximum-round stops'] }], 'half'),
      D('knowledge_gap_queue', 'Knowledge-gap queue',
        'Missing business or agent expertise surfaced for owner action without exposing '
        + 'internal memory folders.',
        [{ kind: 'table', columns: ['Agent', 'Gap', 'Occurrences', 'Owner action'] }], 'half'),
    ],
  },

  // ── 7 · INTEGRATIONS — no sub-tabs, 5 panels ─────────────────────────────
  {
    key: 'integrations', label: 'Integrations', subTabs: [],
    note: 'External systems stay authoritative for live operational data. Rexy owns '
        + 'continuity and intelligence.',
    panels: [
      D('shared_core', 'Shared core — plugins needed across the domain',
        'This is the minimum common stack before sector-specific systems are added.',
        [{ kind: 'matrix', columns: ['Reception', 'Admin', 'Sales', 'CEO'] }], 'full', 'P1 launch core'),
      D('sector_matrices', 'Sector matrices',
        'Each sector adds only the systems needed for its real operating workflow.',
        [{ kind: 'matrix', columns: ['Reception', 'Admin', 'Sales', 'CEO'] }], 'full', '4-sector scope'),
      D('live_vs_post_call_ownership', 'Live-call vs post-call ownership',
        'Keeps latency low and stops unnecessary agent-to-agent hops during a phone call.',
        [{ kind: 'table', columns: ['Situation', 'Owner', 'Why'] }], 'full', 'hard boundary'),
      D('memory_plugin_boundary', 'Memory + plugin boundary',
        'External systems stay authoritative for live operational data. Rexy owns '
        + 'continuity and intelligence.', [
          { kind: 'metrics', rows: ['Current appointment', 'Current job state',
            'Approved rules and pricing', 'Customer preferences / history'] },
          { kind: 'flow', steps: [
            { label: 'Tier-1', sub: 'approved doctrine' },
            { label: 'Tier-2', sub: 'experience' },
            { label: 'Live plugins', sub: 'CRM · calendar · jobs · payments' },
            { label: 'MCR', sub: 'composed context' },
            { label: 'Frontier model', sub: 'reason + act' } ] },
        ], 'full', 'MCR-safe'),
      D('plugin_build_order', 'Plugin build order',
        'Build shared launch dependencies first, then sector-specific operational systems.',
        [{ kind: 'table', columns: ['Priority', 'Integration', 'Agent ownership', 'Sector',
          'Purpose'] }], 'full', 'implementation order'),
    ],
  },

  // ── 8 · SUPPORTING SYSTEMS — 3 sub-tabs, 12 panels ────────────────────────
  // SERVER MONITORING IS INTENTIONALLY HELD OPEN. The design says so explicitly,
  // and nothing beyond what it carries is invented here — the page states the
  // hold and waits for the owner's selection. Importing a feature set to make the
  // tab look finished is the exact thing it is held open against.
  {
    key: 'supporting_systems', label: 'Supporting Systems', panels: [],
    note: 'External documentation remains a live source. A detected change creates a '
        + 'sourced operational record and a repair queue; it never silently rewrites Tier-1.',
    subTabs: [
      { key: 'telco_management', label: 'Telco Management', panels: [
        { ...D('telco_kpis', 'Telco totals', undefined, [{ kind: 'kpis', tiles: [
            { label: 'Provisioned numbers', sub: 'one dedicated identity per business' },
            { label: 'Healthy now' },
            { label: 'Calls today', sub: 'all numbers combined' },
            { label: 'Number cost today', sub: 'transport estimate' },
            { label: 'Emergency registry', sub: 'verified fallback destinations' },
          ] }], 'full'), chrome: true },
        D('unified_number_dashboard', 'Unified Number Dashboard',
          'One tenant-safe view of every dedicated business number, assigned receptionist, '
          + 'geographic region and real-time status.',
          [{ kind: 'table', columns: ['Number', 'Business / agent', 'Region', 'Status',
            'Calls', 'Cost'] }], 'full', 'live sample'),
        D('realtime_number_status', 'Real-Time Number Status',
          'Health state is determined by carrier reachability, inbound route response and '
          + 'receptionist availability.', [
            { kind: 'metrics', rows: ['Healthy', 'Degraded', 'Offline'] },
            { kind: 'pills', items: ['NUMBER-HEALTH', 'ROUTE-CHECK', 'FAILOVER-VERIFY', 'AUDIT-DEPOSIT'] },
          ], 'half', '1 degraded'),
        D('call_volume_analytics', 'Call Volume Analytics', 'Inbound demand by dedicated number and hour.',
          [{ kind: 'volume', hours: ['7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p',
            '3p', '4p', '5p', '6p'] }], 'half'),
        D('geographic_distribution', 'Geographic Distribution View',
          'Numbers and assigned business tenants by Australian operating region.',
          [{ kind: 'table', columns: ['Region', 'Numbers', 'Calls', 'Status'] }], 'half'),
        D('cost_by_number', 'Cost Tracking by Number',
          'Number-specific transport cost feeds receptionist budgets and CEO package optimisation.',
          [{ kind: 'table', columns: ['Business', 'Number', 'Cost today', 'Trend'] }], 'half'),
        D('emergency_registry', 'Emergency Number Registry',
          'Approved emergency and fallback destinations are tenant-specific, source-controlled '
          + 'and verified before use.',
          [{ kind: 'table', columns: ['Business', 'Trigger', 'Primary destination',
            'Fallback chain', 'Last verified'] }], 'full', 'protected'),
      ] },
      { key: 'api_monitoring', label: 'API Monitoring', panels: [
        { ...D('api_kpis', 'API totals', undefined, [{ kind: 'kpis', tiles: [
            { label: 'APIs monitored', sub: 'voice, CRM, PMS, calendar, payment' },
            { label: 'Doc changes' },
            { label: 'High priority' },
            { label: 'Auth changes', sub: 'scope and token checks' },
            { label: 'Alert emails', sub: 'delivered to owners / engineering' },
          ] }], 'full'), chrome: true },
        D('api_monitoring_path', 'API monitoring path',
          'External documentation remains a live source. A detected change creates a sourced '
          + 'operational record and repair queue; it never silently rewrites Tier-1.',
          [{ kind: 'flow', steps: [
            { label: 'Documentation Change Detection', sub: 'scheduled source diff' },
            { label: 'Authentication Watch', sub: 'OAuth, keys, signatures' },
            { label: 'Priority Classification', sub: 'critical → low' },
            { label: 'Alert Email Notifications', sub: 'owners / engineering' },
            { label: 'Sourced repair record', sub: 'never a silent T1 rewrite' } ] }], 'full', 'deterministic'),
        D('doc_change_detection', 'Documentation Change Detection',
          'Change records with exact source, detection time, affected connector and classified impact.',
          [{ kind: 'table', columns: ['Provider', 'Detected change', 'Affected system',
            'Priority', 'State'] }], 'full', 'sample data'),
        D('priority_classification', 'Priority Classification',
          'Classification is code-backed and based on affected capability, deadline, security '
          + 'exposure and customer impact.', [
            { kind: 'metrics', rows: ['Critical', 'High', 'Medium', 'Low'] },
            { kind: 'pills', items: ['DOC-DIFF', 'IMPACT-MAP', 'PRIORITY-CLASSIFIER', 'SOURCE-KEEPER'] },
          ], 'half'),
        D('alert_email_notifications', 'Alert Email Notifications',
          'Alerts are operational messages with source links, affected tenant/system, priority, '
          + 'owner and required action.',
          [{ kind: 'table', columns: ['Time', 'Recipient group', 'Subject', 'Status'] }], 'half'),
        D('auth_change_detection', 'Authentication Change Detection',
          'Monitors token endpoints, OAuth scopes, API-key requirements, signing headers and '
          + 'credential-rotation rules.',
          [{ kind: 'table', columns: ['Control', 'Previous', 'Detected', 'Required response'] }], 'full', '1 active'),
      ] },
      { key: 'server_monitoring', label: 'Server Monitoring', panels: [
        D('server_monitoring_system', 'Server Monitoring System',
          'The source case study contains a third supporting-system category, but no '
          + 'server-monitoring features have been selected for this Rexy build yet.',
          [{ kind: 'note', text: 'This page is intentionally held open rather than importing '
            + 'all twenty source features. It will be populated only with the server-monitoring '
            + 'capabilities the owner explicitly chooses.' }], 'full', 'selection pending'),
      ] },
    ],
  },

  // ── 9 · PRICING + BUDGETS — no sub-tabs, 1 panel ──────────────────────────
  {
    key: 'pricing_budgets', label: 'Pricing + Budgets', subTabs: [],
    note: 'Approved commercial configuration. The CEO and DreamState may recommend a '
        + 'review; neither ever silently changes billing.',
    panels: [
      D('per_receptionist_cost_watch', 'Per-receptionist cost watch',
        'CEO uses call duration, sector, admin volume and Twilio/model estimates to '
        + 'recommend package scaling.',
        [{ kind: 'table', columns: ['Agent', 'Sector', 'Projected minutes', 'Current tier',
          'Recommendation'] }], 'full'),
    ],
  },

  // ── 10 · OSIRIS — no sub-tabs, 3 panels · NO OWNING AGENT · NOTHING FED ────
  //
  // THE FIRST ROOT ON THIS SURFACE THE DESIGN DOES NOT DRAW. Every other root
  // here was transcribed from the v0.11 HTML. That file contains no Osiris —
  // nothing named it anywhere in the repo but one line of Forge's, listing
  // "Osiris.io" among what was NOT built. So this is a shell authored to the
  // owner's brief, and it is marked as such: nothing below claims to be the
  // design's, because the design has no opinion about it yet.
  //
  // IT IS AN APP, NOT AN AGENT. No chat, no turns, no agent id, no memory. That
  // is achieved by OMISSION — no ROLE_ROOT entry — so `rootOwner` is null and the
  // DASHBOARD | CHAT bar never renders. See the note on ROLE_ROOT.
  //
  // WHY A ROOT AND NOT A TAB. It reads Marketing and Sales and belongs to
  // neither. As a tab inside either one it would inherit that root's board
  // address and read as that agent's view of the map; the whole point is that it
  // is nobody's.
  //
  // NOTHING IS FED AND NOTHING FAKES BEING FED. There is no board read wired
  // here beyond the address every declared panel already has, and
  // `core/harness_topology.py` was deliberately NOT touched — adding "Osiris" to
  // GROWTH's `tabs` tuple is what would make `board.osiris.*` writable, and that
  // is the data wiring that comes after Marketing and Sales, on the backend
  // side. Until then every panel reads a slot the server never writes, which
  // renders as "awaiting first write" and an em dash per value. That is the
  // honest state, not a gap.
  {
    key: 'osiris', label: 'Osiris', subTabs: [],
    note: 'Cross-domain discovery map. Reads Marketing and Sales; owned by neither, '
        + 'and not an agent — there is nothing here to talk to. Structure only: no '
        + 'board is wired to this root yet.',
    panels: [
      // The map is REAL and empty, never a caption about a map. Zero pins draws
      // the continent the lanes are scoped to, pannable — see DiscoveryMap.tsx,
      // which reads no endpoint and takes its pins as a prop it is handed. With
      // no board written, that prop is undefined and zero pins is the truth.
      D('osiris_map', 'Discovery map',
        'Every candidate Marketing has found, on one board. Pan and zoom are the '
        + 'browser\'s and reach nothing — there is no "search this area" because '
        + 'there is no route for it to post to.',
        [{ kind: 'map' }], 'full'),

      // THE SECTOR LANES — five, named by the owner, and DELIBERATELY UNTONED.
      // The design assigns sectors no colour: 163 elements in the v0.11 HTML name
      // a sector and not one carries a sector-keyed tone. The two that looked
      // like it are neither — `cal-event blue` tracks the EVENT KIND (a mechanic
      // and a vet are both teal under "Consult"), and the one teal pill sits on a
      // TENANT ID, whose sibling id is untoned two cells away in the same table.
      // So there is nothing to transcribe, and picking five colours here would be
      // this file inventing a vocabulary the design never set. Reported instead.
      // (The tenant ids are deliberately not written out here: the surface must
      //  carry no design sample name, and the acceptance greps this file raw.)
      //
      // IT IS A FILTER NOW. `DiscoveryPin` carries `sector`, derived by
      // server/growth_places.py from each record's own scope_path, so "show me
      // only locksmiths" is a question the board can answer and the lanes are
      // clickable. THE PREVIOUS NOTE HERE SAID THE OPPOSITE and was left
      // standing after the field landed; it is corrected rather than kept.
      //
      // THE NAMES ARE THE STORE'S, COUNTED NOT ASSUMED — see osiris/pin.ts for
      // the counts. 'Removalists' was carried here from the earlier Mission
      // Control scope and appears in NO record and no manual; 'Mobile VETs' is
      // in the store with T1 manuals across all eight states and was missing.
      // 'VETs' and 'Mobile Mechanics' are the store's exact byte forms — the
      // capitalisation is not a typo, and a lane whose name is one character out
      // filters nothing while looking perfectly healthy.
      //
      // PROVISIONAL: Forge is declaring one canonical sector vocabulary. This
      // list and pin.ts's both collapse into it when it lands.
      D('osiris_sectors', 'Sector lanes',
        'The five lanes discovery runs in, each with its own colour. A pin '
        + 'whose record carries no lane renders unfiled rather than hidden.',
        [
          { kind: 'pills', items: ['Locksmiths', 'VETs', 'Mobile VETs',
            'Mechanics', 'Mobile Mechanics'] },
          { kind: 'metrics', rows: ['Lanes declared', 'Pins mapped',
            'Pins without coordinates'] },
        ], 'half'),

      // PIN DETAIL — the fields are THE PIN CONTRACT, not a wish list. Each row
      // is a real property of DiscoveryPin, so when the producer writes pins this
      // panel is already addressing fields that exist. Sector and source are
      // absent BECAUSE THE CONTRACT HAS NEITHER; adding them here would declare a
      // shape the producer never agreed to.
      D('osiris_pin', 'Pin detail',
        'The selected pin. Selection is not wired — marker clicks are an action '
        + 'and have no endpoint, so no pin can be selected and every field reads '
        + 'em dash.',
        [{ kind: 'metrics', rows: ['Name', 'Address', 'Latitude', 'Longitude',
          'Record id'] }], 'half'),
    ],
  },
];

/**
 * THE SURFACE'S OWN BRAND LINE — a property of THIS SURFACE, not of whichever
 * domain is currently routed to it.
 *
 * Owner correction (in-terminal, 2026-08-19): the header read "GROWTH domain"
 * where the app reads "Frontier domain". The design's brand line is fixed text
 * in the same class as "Rexy Receptionist" and "v0.11" — it names the surface,
 * not the data below it.
 *
 * FLAGGED HONESTLY: this is the one string on the surface that does not derive
 * from what the API returned, and it does say a domain name. It sits here rather
 * than inside a component so it is a single declared constant a reviewer can see
 * and change, not a literal buried in JSX. If a second domain is ever routed to
 * this surface, this line still reads "Frontier" and would need a ruling.
 */
export const SURFACE_BRAND = {
  name: 'Rexy Receptionist',
  context: 'Frontier domain',
  version: 'Backend mission control v0.11',
} as const;

/** The page title's subtitle — the owner's own line, verbatim. */
export const SURFACE_SUBTITLE =
  'One operating surface for CEO reporting, marketing, sales, call reception, ' +
  'admin, fleet agents, memory loops and budget control';

/**
 * Total panels the design carries, for the build ledger.
 *
 * THE ITEMISED LIST IS THE TRUTH: 118, not 120 (owner correction 2026-08-19 —
 * the headline was wrong and the per-root numbers are right). Overview 7 · CEO 13
 * · Marketing 15 · Sales 51 · Company Agents 11 · Admin Fleet 3 · Integrations 5
 * · Supporting Systems 12 · Pricing 1.
 */
export function panelLedger() {
  const real = (ps: PanelSpec[]) => ps.filter((p) => !p.chrome).length;
  let declared = 0, awaiting = 0, subTabs = 0, chrome = 0;
  for (const r of SURFACE) {
    declared += real(r.panels); chrome += r.panels.length - real(r.panels);
    awaiting += r.awaiting || 0; subTabs += r.subTabs.length;
    for (const t of r.subTabs) {
      declared += real(t.panels); chrome += t.panels.length - real(t.panels);
      awaiting += t.awaiting || 0;
      for (const v of t.views || []) {
        declared += real(v.panels); chrome += v.panels.length - real(v.panels);
      }
    }
  }
  return { roots: SURFACE.length, subTabs, declared, awaiting, chrome, total: declared + awaiting };
}

/**
 * PARITY WITH THE TOPOLOGY. The board store is keyed by the slug of each root
 * LABEL, derived server-side by core/harness_topology.slug. If a key below ever
 * stops matching that derivation, a panel silently reads a key the server never
 * writes — which looks exactly like "no data yet". Exported so it can be
 * asserted rather than assumed.
 */
export function assertParity(slugOf: (label: string) => string): string[] {
  const bad: string[] = [];
  for (const r of SURFACE) {
    const derived = slugOf(r.label);
    if (derived !== r.key) bad.push(`${r.label}: declared "${r.key}" but slug() gives "${derived}"`);
  }
  return bad;
}
